/**
 * Single source of truth for AI model configuration.
 * This module defines all model-related constants and mappings used throughout the frontend.
 * Must be kept in sync with api/common/model_config.py
 */

export interface ModelInfo {
  apiName: string;  // The key used in API calls (e.g., 'gpt-4o')
  llmName: string;  // The actual model name for the LLM provider (e.g., 'gpt-4o')
  displayName: string;  // Human-readable name for UI (e.g., 'GPT-4 Optimized')
  provider: string;  // Provider name (e.g., 'OpenAI')
  category: 'text' | 'embedding' | 'image' | 'audio' | 'video';  // Model category
  isAvailable?: boolean;  // Whether the model is currently available for use
  supportsVision?: boolean;  // Whether the model supports image inputs
  supportsFunctionCalling?: boolean;  // Whether the model supports function calling
  contextWindow?: number;  // Maximum context window size
  description?: string;  // Optional description of the model
  costRating?: number;  // 1 (cheapest) to 5 (most expensive) — relative within category
  speedRating?: number;  // 1 (slowest) to 5 (fastest)
  intelligenceRating?: number;  // 1 (basic) to 5 (most capable)
  // True iff this model can be served under Zero Data Retention (ZDR /
  // privacy / "no-train" mode): routed through Vertex AI with US data
  // residency. Mirrors `ZDR_ELIGIBLE_MODELS` in api/common/model_config.py.
  zeroDataRetention?: boolean;
  // Base model when this entry is a preset of it (see MODEL_VARIANTS).
  variantOf?: string;
}

// Complete model registry with all information
export const MODEL_REGISTRY: Record<string, ModelInfo> = {
  // OpenAI Models
  'gpt35t': {
    apiName: 'gpt35t',
    llmName: 'gpt-3.5-turbo',
    displayName: 'GPT-3.5 Turbo',
    provider: 'OpenAI',
    category: 'text',
    contextWindow: 16385,
    description: 'Deprecated 2026-10-23 — auto-remapped to GPT-6 Luna',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 5, intelligenceRating: 1,
  },
  'gpt-4o': {
    apiName: 'gpt-4o',
    llmName: 'gpt-4o',
    displayName: 'GPT-4 Optimized',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 128000,
    description: 'Most capable GPT-4 model, optimized for speed',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 3, speedRating: 3, intelligenceRating: 3,
  },
  'gpt-4o-mini': {
    apiName: 'gpt-4o-mini',
    llmName: 'gpt-4o-mini',
    displayName: 'GPT-4 Optimized Mini',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 128000,
    description: 'Smaller, faster, cheaper GPT-4 model',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 4, intelligenceRating: 2,
  },
  'o3-mini': {
    apiName: 'o3-mini',
    llmName: 'gpt-4o-mini',
    displayName: 'O3 Mini (deprecated)',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 128000,
    description: 'Deprecated 2026-10-23 — auto-remapped to GPT-5.4 Mini',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 2, speedRating: 3, intelligenceRating: 3,
  },
  'gpt-4.1': {
    apiName: 'gpt-4.1',
    llmName: 'gpt-4o',
    displayName: 'GPT-4.1',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 128000,
    description: 'Next generation GPT-4',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 3, speedRating: 3, intelligenceRating: 3,
  },
  'gpt-4.1-mini': {
    apiName: 'gpt-4.1-mini',
    llmName: 'gpt-4o-mini',
    displayName: 'GPT-4.1 Mini',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 128000,
    description: 'Next generation GPT-4 mini',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 4, intelligenceRating: 2,
  },
  'o4-mini': {
    apiName: 'o4-mini',
    llmName: 'o4-mini',
    displayName: 'O4 Mini (deprecated)',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 200000,
    description: 'Deprecated 2026-10-23 — auto-remapped to GPT-5.4 Mini',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 2, speedRating: 3, intelligenceRating: 3,
  },
  'gpt-5': {
    apiName: 'gpt-5',
    llmName: 'gpt-5',
    displayName: 'GPT-5 (deprecated)',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 128000,
    description: 'Deprecated 2026-12-11 — auto-remapped to GPT-6 Sol',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 3, speedRating: 3, intelligenceRating: 4,
  },
  'gpt-5.1': {
    apiName: 'gpt-5.1',
    llmName: 'gpt-5.1',
    displayName: 'GPT-5.1 (deprecated)',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 128000,
    description: 'Deprecated 2027-04-01 — auto-remapped to GPT-6 Sol',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 3, speedRating: 3, intelligenceRating: 4,
  },
  'gpt-5.2': {
    apiName: 'gpt-5.2',
    llmName: 'gpt-5.2',
    displayName: 'GPT-5.2',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 128000,
    description: 'Latest GPT-5 series model',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 4, speedRating: 2, intelligenceRating: 5,
  },
  'gpt-5-mini': {
    apiName: 'gpt-5-mini',
    llmName: 'gpt-5-mini',
    displayName: 'GPT-5 Mini (deprecated)',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 128000,
    description: 'Deprecated 2026-12-11 — auto-remapped to GPT-6 Luna',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 4, intelligenceRating: 3,
  },
  'gpt-5-nano': {
    apiName: 'gpt-5-nano',
    llmName: 'gpt-5-nano',
    displayName: 'GPT-5 Nano (deprecated)',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 128000,
    description: 'Deprecated 2026-12-11 — auto-remapped to GPT-6 Luna',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 5, intelligenceRating: 2,
  },
  'gpt-5-pro': {
    apiName: 'gpt-5-pro',
    llmName: 'gpt-5-pro',
    displayName: 'GPT-5 Pro (deprecated)',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 128000,
    description: 'Deprecated 2026-12-11 — auto-remapped to GPT-6 Astra',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 5, speedRating: 1, intelligenceRating: 5,
  },
  'gpt-4.1-nano': {
    apiName: 'gpt-4.1-nano',
    llmName: 'gpt-4.1-nano',
    displayName: 'GPT-4.1 Nano (deprecated)',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 128000,
    description: 'Deprecated 2026-10-23 — auto-remapped to GPT-6 Luna',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 5, intelligenceRating: 1,
  },
  'gpt-5.3': {
    apiName: 'gpt-5.3',
    llmName: 'gpt-5.3',
    displayName: 'GPT-5.3',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 128000,
    description: 'Latest GPT-5.3 model',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 4, speedRating: 2, intelligenceRating: 5,
  },
  'gpt-5.4-mini': {
    apiName: 'gpt-5.4-mini',
    llmName: 'gpt-5.4-mini',
    displayName: 'GPT-5.4 Mini',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 400000,
    description: 'GPT-5.4 mini — fast, multimodal, agentic',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 2, speedRating: 4, intelligenceRating: 4,
  },
  'gpt-5.4-nano': {
    apiName: 'gpt-5.4-nano',
    llmName: 'gpt-5.4-nano',
    displayName: 'GPT-5.4 Nano (deprecated)',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 400000,
    description: 'Deprecated 2027-04-01 — auto-remapped to GPT-6 Luna',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 5, intelligenceRating: 3,
  },
  'gpt-5.5': {
    apiName: 'gpt-5.5',
    llmName: 'gpt-5.5',
    displayName: 'GPT-5.5',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 400000,
    description: 'GPT-5.5 — OpenAI flagship for complex reasoning and coding',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 5, speedRating: 2, intelligenceRating: 5,
  },
  'gpt-5.5-pro': {
    apiName: 'gpt-5.5-pro',
    llmName: 'gpt-5.5-pro',
    displayName: 'GPT-5.5 Pro',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 400000,
    description: 'GPT-5.5 Pro — highest-accuracy GPT-5.5 for the hardest tasks',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 5, speedRating: 1, intelligenceRating: 5,
  },
  'gpt-5.4': {
    apiName: 'gpt-5.4',
    llmName: 'gpt-5.4',
    displayName: 'GPT-5.4',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 400000,
    description: 'GPT-5.4 — OpenAI flagship of the 5.4 family',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 4, speedRating: 3, intelligenceRating: 5,
  },
  'gpt-5.4-pro': {
    apiName: 'gpt-5.4-pro',
    llmName: 'gpt-5.4-pro',
    displayName: 'GPT-5.4 Pro',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 400000,
    description: 'GPT-5.4 Pro — highest-accuracy 5.4 for mission-critical work',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 5, speedRating: 1, intelligenceRating: 5,
  },
  'gpt-5.6-sol': {
    apiName: 'gpt-5.6-sol',
    llmName: 'gpt-5.6-sol',
    displayName: 'GPT-5.6 Sol',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 400000,
    description: 'GPT-5.6 Sol — OpenAI 5.6 flagship for complex reasoning and coding',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 5, speedRating: 2, intelligenceRating: 5,
  },
  'gpt-5.6-terra': {
    apiName: 'gpt-5.6-terra',
    llmName: 'gpt-5.6-terra',
    displayName: 'GPT-5.6 Terra',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 400000,
    description: 'GPT-5.6 Terra — balanced intelligence and cost',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 3, speedRating: 3, intelligenceRating: 5,
  },
  'gpt-5.6-luna': {
    apiName: 'gpt-5.6-luna',
    llmName: 'gpt-5.6-luna',
    displayName: 'GPT-5.6 Luna',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 400000,
    description: 'GPT-5.6 Luna — cost-optimized for high-volume work',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 5, intelligenceRating: 4,
  },
  'gpt-6-astra': {
    apiName: 'gpt-6-astra',
    llmName: 'gpt-6-astra',
    displayName: 'GPT-6 Astra',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 1050000,
    description: "GPT-6 Astra — OpenAI's most capable model for the hardest reasoning and coding",
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 5, speedRating: 2, intelligenceRating: 5,
  },
  'gpt-6.1-sol': {
    apiName: 'gpt-6.1-sol',
    llmName: 'gpt-6.1-sol',
    displayName: 'GPT-6.1 Sol',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 1050000,
    description: 'GPT-6.1 Sol — near-Astra quality at Sol pricing',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 3, speedRating: 3, intelligenceRating: 5,
  },
  'gpt-6-sol': {
    apiName: 'gpt-6-sol',
    llmName: 'gpt-6-sol',
    displayName: 'GPT-6 Sol',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 1050000,
    description: 'GPT-6 Sol — strong general model, fast for its tier',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 3, speedRating: 4, intelligenceRating: 5,
  },
  'gpt-6-luna': {
    apiName: 'gpt-6-luna',
    llmName: 'gpt-6-luna',
    displayName: 'GPT-6 Luna',
    provider: 'OpenAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 1050000,
    description: 'GPT-6 Luna — cheapest current OpenAI model for high-volume work',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 4, intelligenceRating: 4,
  },
  
  // Gemini Models
  // Gemini 1.5 family — retired by Google in late 2025. Kept in the
  // registry so historical AI functions still render a display name but
  // marked NOT available and remapped at runtime by DEPRECATED_MODELS.
  'gemini': {
    apiName: 'gemini',
    llmName: 'gemini-1.5-flash',
    displayName: 'Gemini Flash (deprecated)',
    provider: 'Google',
    category: 'text',
    supportsVision: true,
    contextWindow: 1048576,
    description: 'Deprecated — auto-remapped to Gemini 3.1 Flash Lite',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 5, intelligenceRating: 2,
  },
  // Gemini 2.0 Flash — Vertex AI retirement 2026-06-01. Same treatment.
  'gemini-2': {
    apiName: 'gemini-2',
    llmName: 'gemini-2.0-flash',
    displayName: 'Gemini 2.0 Flash (deprecated)',
    provider: 'Google',
    category: 'text',
    supportsVision: true,
    contextWindow: 1048576,
    description: 'Deprecated 2026-06-01 — auto-remapped to Gemini 3.1 Flash Lite',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 4, intelligenceRating: 2,
  },
  'gemini-pro': {
    apiName: 'gemini-pro',
    llmName: 'gemini-1.5-pro',
    displayName: 'Gemini Pro (deprecated)',
    provider: 'Google',
    category: 'text',
    supportsVision: true,
    contextWindow: 2097152,
    description: 'Deprecated — auto-remapped to Gemini 3.5 Flash',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 3, speedRating: 2, intelligenceRating: 3,
  },
  'gemini-2-5-flash': {
    apiName: 'gemini-2-5-flash',
    llmName: 'gemini-2.5-flash',
    displayName: 'Gemini 2.5 Flash (deprecated)',
    provider: 'Google',
    category: 'text',
    supportsVision: true,
    contextWindow: 1048576,
    description: 'Deprecated 2026-10-20 — auto-remapped to Gemini 3.5 Flash-Lite',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 4, intelligenceRating: 3,
  },
  'gemini-2-5-pro': {
    apiName: 'gemini-2-5-pro',
    llmName: 'gemini-2.5-pro',
    displayName: 'Gemini 2.5 Pro (deprecated)',
    provider: 'Google',
    category: 'text',
    supportsVision: true,
    contextWindow: 2097152,
    description: 'Deprecated 2026-10-20 — auto-remapped to Gemini 3.5 Flash',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 3, speedRating: 2, intelligenceRating: 4,
  },
  'gemini-3-pro-preview': {
    apiName: 'gemini-3-pro-preview',
    llmName: 'gemini-3-pro-preview',
    displayName: 'Gemini 3 Pro Preview',
    provider: 'Google',
    category: 'text',
    supportsVision: true,
    contextWindow: 2097152,
    description: 'Preview of Gemini 3 Pro (deprecated)',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 4, speedRating: 2, intelligenceRating: 4,
  },
  'gemini-3-flash-preview': {
    apiName: 'gemini-3-flash-preview',
    llmName: 'gemini-3-flash-preview',
    displayName: 'Gemini 3 Flash Preview',
    provider: 'Google',
    category: 'text',
    supportsVision: true,
    contextWindow: 1048576,
    description: 'Gemini 3 Flash preview — prefer Gemini 3.5/3.7 Flash for GA',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 2, speedRating: 4, intelligenceRating: 4,
  },
  'gemini-2-5-flash-lite': {
    apiName: 'gemini-2-5-flash-lite',
    llmName: 'gemini-2.5-flash-lite',
    displayName: 'Gemini 2.5 Flash Lite (deprecated)',
    provider: 'Google',
    category: 'text',
    supportsVision: true,
    contextWindow: 1048576,
    description: 'Deprecated 2026-10-20 — auto-remapped to Gemini 3.1 Flash Lite',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 5, intelligenceRating: 2,
  },
  'gemini-3-1-pro-preview': {
    apiName: 'gemini-3-1-pro-preview',
    llmName: 'gemini-3.1-pro-preview',
    displayName: 'Gemini 3.1 Pro Preview',
    provider: 'Google',
    category: 'text',
    supportsVision: true,
    contextWindow: 2097152,
    description: 'Gemini 3.1 Pro — replaces deprecated 3 Pro Preview',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 4, speedRating: 2, intelligenceRating: 5,
  },
  'gemini-3-1-flash-preview': {
    apiName: 'gemini-3-1-flash-preview',
    llmName: 'gemini-3.1-flash-preview',
    displayName: 'Gemini 3.1 Flash Preview',
    provider: 'Google',
    category: 'text',
    supportsVision: true,
    contextWindow: 1048576,
    description: 'NOT AVAILABLE — use Gemini 3 Flash or 3.1 Flash-Lite instead',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 2, speedRating: 4, intelligenceRating: 4,
  },
  'gemini-3-1-flash-lite': {
    apiName: 'gemini-3-1-flash-lite',
    llmName: 'gemini-3.1-flash-lite',
    displayName: 'Gemini 3.1 Flash Lite',
    provider: 'Google',
    category: 'text',
    supportsVision: true,
    contextWindow: 1048576,
    description: 'Fastest, most cost-efficient Gemini 3 model (GA)',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 5, intelligenceRating: 3,
  },
  'gemini-3-5-flash': {
    apiName: 'gemini-3-5-flash',
    llmName: 'gemini-3.5-flash',
    displayName: 'Gemini 3.5 Flash',
    provider: 'Google',
    category: 'text',
    supportsVision: true,
    contextWindow: 1048576,
    description: 'Near-Pro intelligence at Flash-tier cost and speed',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 3, speedRating: 4, intelligenceRating: 5,
  },
  'gemini-3-5-flash-lite': {
    apiName: 'gemini-3-5-flash-lite',
    llmName: 'gemini-3.5-flash-lite',
    displayName: 'Gemini 3.5 Flash Lite',
    provider: 'Google',
    category: 'text',
    supportsVision: true,
    contextWindow: 1048576,
    description: 'High-volume Flash-Lite — successor to Gemini 2.5 Flash',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 2, speedRating: 5, intelligenceRating: 4,
  },
  'gemini-3-7-flash': {
    apiName: 'gemini-3-7-flash',
    llmName: 'gemini-3.7-flash',
    displayName: 'Gemini 3.7 Flash',
    provider: 'Google',
    category: 'text',
    supportsVision: true,
    contextWindow: 1048576,
    description: 'Current Gemini Flash workhorse — Pro-level agentic coding at Flash cost',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 2, speedRating: 4, intelligenceRating: 5,
  },
  'gemini-3-flash': {
    apiName: 'gemini-3-flash',
    llmName: 'gemini-3-flash-preview',
    displayName: 'Gemini 3 Flash',
    provider: 'Google',
    category: 'text',
    supportsVision: true,
    contextWindow: 1048576,
    description: 'Gemini 3 Flash — strong reasoning, preview-only upstream',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 2, speedRating: 4, intelligenceRating: 4,
  },
  'gemini-3-6-flash': {
    apiName: 'gemini-3-6-flash',
    llmName: 'gemini-3.6-flash',
    displayName: 'Gemini 3.6 Flash',
    provider: 'Google',
    category: 'text',
    supportsVision: true,
    contextWindow: 1048576,
    description: 'Gemini 3.6 Flash (GA) — Flash quality at intro Vertex pricing',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 2, speedRating: 4, intelligenceRating: 5,
  },
  'gemini-3-8-flash': {
    apiName: 'gemini-3-8-flash',
    llmName: 'gemini-3.8-flash',
    displayName: 'Gemini 3.8 Flash',
    provider: 'Google',
    category: 'text',
    supportsVision: true,
    contextWindow: 1048576,
    description: 'Newest Gemini Flash (GA) — intro Vertex pricing through 2026-12-31',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 2, speedRating: 4, intelligenceRating: 5,
  },
  'gemma-4': {
    apiName: 'gemma-4',
    llmName: 'gemma-4-26b-a4b-it-maas',
    displayName: 'Gemma 4',
    provider: 'Google',
    category: 'text',
    supportsVision: true,
    contextWindow: 131072,
    description: 'Gemma 4 26B — open model on Vertex AI MaaS, very low cost, multimodal',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 4, intelligenceRating: 3,
  },
  
  // Removed unimplemented models:
  // groq-gemma, groq-llama, cerebras-8b, cerebras-70b
  // These do not have working implementations
  
  // Other Models
  'grok2': {
    apiName: 'grok2',
    llmName: 'grok2',
    displayName: 'Grok 2 (deprecated)',
    provider: 'xAI',
    category: 'text',
    contextWindow: 8192,
    description: 'Retired — auto-remapped to Grok 4.3',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 3, speedRating: 3, intelligenceRating: 3,
  },
  'grok3': {
    apiName: 'grok3',
    llmName: 'grok-3-latest',
    displayName: 'Grok 3 (deprecated)',
    provider: 'xAI',
    category: 'text',
    contextWindow: 128000,
    description: 'Retired 2026-05-15 — auto-remapped to Grok 4.3',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 4, speedRating: 2, intelligenceRating: 4,
  },
  'grok3-mini': {
    apiName: 'grok3-mini',
    llmName: 'grok-3-mini',
    displayName: 'Grok 3 Mini (deprecated)',
    provider: 'xAI',
    category: 'text',
    contextWindow: 128000,
    description: 'Retired 2026-05-15 — auto-remapped to GPT-6 Luna',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 4, intelligenceRating: 3,
  },
  'grok4': {
    apiName: 'grok4',
    llmName: 'grok-4-latest',
    displayName: 'Grok 4 (deprecated)',
    provider: 'xAI',
    category: 'text',
    contextWindow: 2000000,
    description: 'Retired 2026-05-15 — auto-remapped to Grok 4.3',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 4, speedRating: 2, intelligenceRating: 5,
  },
  'grok4-fast': {
    apiName: 'grok4-fast',
    llmName: 'grok-4-fast',
    displayName: 'Grok 4 Fast (deprecated)',
    provider: 'xAI',
    category: 'text',
    contextWindow: 2000000,
    description: 'Retired 2026-05-15 — auto-remapped to GPT-6 Luna',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 5, intelligenceRating: 3,
  },
  'grok4-20': {
    apiName: 'grok4-20',
    llmName: 'grok-4.20',
    displayName: 'Grok 4.20',
    provider: 'xAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 2000000,
    description: 'xAI\'s Grok 4.20 reasoning model',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 3, speedRating: 3, intelligenceRating: 5,
  },
  'grok4-1-fast': {
    apiName: 'grok4-1-fast',
    llmName: 'xai/grok-4.1-fast-non-reasoning',
    displayName: 'Grok 4.1 Fast',
    provider: 'xAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 2000000,
    description: 'xAI\'s fastest Grok, via Vertex AI Model Garden',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 5, intelligenceRating: 3,
  },
  'grok4-3': {
    apiName: 'grok4-3',
    llmName: 'grok-4.3',
    displayName: 'Grok 4.3',
    provider: 'xAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 256000,
    description: 'xAI\'s Grok 4.3 flagship chat and coding model',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 2, speedRating: 3, intelligenceRating: 4,
  },
  'grok4-5': {
    apiName: 'grok4-5',
    llmName: 'grok-4.5',
    displayName: 'Grok 4.5',
    provider: 'xAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 500000,
    description: 'xAI\'s Grok 4.5, long-context chat and coding',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 3, speedRating: 3, intelligenceRating: 5,
  },
  'grok4-6': {
    apiName: 'grok4-6',
    llmName: 'grok-4.6',
    displayName: 'Grok 4.6',
    provider: 'xAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 500000,
    description: 'xAI\'s Grok 4.6, long-context chat and coding',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 3, speedRating: 3, intelligenceRating: 5,
  },
  'grok4-7': {
    apiName: 'grok4-7',
    llmName: 'grok-4.7',
    displayName: 'Grok 4.7',
    provider: 'xAI',
    category: 'text',
    supportsVision: true,
    contextWindow: 500000,
    description: 'xAI\'s latest Grok, long-context chat and coding',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 3, speedRating: 3, intelligenceRating: 5,
  },
  // Anthropic Claude Models (via Vertex AI Model Garden)
  'claude-sonnet-4': {
    apiName: 'claude-sonnet-4',
    llmName: 'claude-sonnet-4',
    displayName: 'Claude Sonnet 4 (deprecated)',
    provider: 'Anthropic',
    category: 'text',
    supportsVision: true,
    contextWindow: 200000,
    description: 'Retired 2026-09-14 — auto-remapped to Claude Sonnet 4.6',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 3, speedRating: 3, intelligenceRating: 4,
  },
  'claude-haiku-4': {
    apiName: 'claude-haiku-4',
    llmName: 'claude-haiku-4',
    displayName: 'Claude Haiku 4 (deprecated)',
    provider: 'Anthropic',
    category: 'text',
    supportsVision: true,
    contextWindow: 200000,
    description: 'Not a real Claude model — auto-remapped to Claude Haiku 4.5',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 5, intelligenceRating: 3,
  },
  'claude-opus-4': {
    apiName: 'claude-opus-4',
    llmName: 'claude-opus-4',
    displayName: 'Claude Opus 4 (deprecated)',
    provider: 'Anthropic',
    category: 'text',
    supportsVision: true,
    contextWindow: 200000,
    description: 'Retired 2026-09-14 — auto-remapped to Claude Opus 4.8',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 5, speedRating: 1, intelligenceRating: 5,
  },
  'claude-sonnet-4-5': {
    apiName: 'claude-sonnet-4-5',
    llmName: 'claude-sonnet-4-5',
    displayName: 'Claude Sonnet 4.5 (deprecated)',
    provider: 'Anthropic',
    category: 'text',
    supportsVision: true,
    contextWindow: 200000,
    description: 'Deprecated 2026-11-30 — auto-remapped to Claude Sonnet 5.5',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 3, speedRating: 3, intelligenceRating: 4,
  },
  'claude-sonnet-4-6': {
    apiName: 'claude-sonnet-4-6',
    llmName: 'claude-sonnet-4-6',
    displayName: 'Claude Sonnet 4.6',
    provider: 'Anthropic',
    category: 'text',
    supportsVision: true,
    contextWindow: 200000,
    description: 'Anthropic\'s Claude Sonnet 4.6 — latest Sonnet via Vertex AI',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 3, speedRating: 3, intelligenceRating: 5,
  },
  'claude-opus-4-1': {
    apiName: 'claude-opus-4-1',
    llmName: 'claude-opus-4-1',
    displayName: 'Claude Opus 4.1 (deprecated)',
    provider: 'Anthropic',
    category: 'text',
    supportsVision: true,
    contextWindow: 200000,
    description: 'Retired 2026-08-05 — auto-remapped to Claude Opus 4.8',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 5, speedRating: 1, intelligenceRating: 5,
  },
  'claude-opus-4-5': {
    apiName: 'claude-opus-4-5',
    llmName: 'claude-opus-4-5',
    displayName: 'Claude Opus 4.5',
    provider: 'Anthropic',
    category: 'text',
    supportsVision: true,
    contextWindow: 200000,
    description: 'Anthropic\'s Claude Opus 4.5 via Vertex AI',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 4, speedRating: 2, intelligenceRating: 5,
  },
  'claude-opus-4-6': {
    apiName: 'claude-opus-4-6',
    llmName: 'claude-opus-4-6',
    displayName: 'Claude Opus 4.6',
    provider: 'Anthropic',
    category: 'text',
    supportsVision: true,
    contextWindow: 200000,
    description: 'Anthropic\'s Claude Opus 4.6 — latest Opus via Vertex AI',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 4, speedRating: 2, intelligenceRating: 5,
  },
  'claude-haiku-4-5': {
    apiName: 'claude-haiku-4-5',
    llmName: 'claude-haiku-4-5',
    displayName: 'Claude Haiku 4.5',
    provider: 'Anthropic',
    category: 'text',
    supportsVision: true,
    contextWindow: 200000,
    description: 'Anthropic\'s Claude Haiku 4.5 — fast and efficient via Vertex AI',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 2, speedRating: 5, intelligenceRating: 3,
  },
  'claude-sonnet-5': {
    apiName: 'claude-sonnet-5',
    llmName: 'claude-sonnet-5',
    displayName: 'Claude Sonnet 5',
    provider: 'Anthropic',
    category: 'text',
    supportsVision: true,
    contextWindow: 200000,
    description: 'Anthropic\'s Claude Sonnet 5 via Vertex AI',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 3, speedRating: 3, intelligenceRating: 5,
  },
  'claude-opus-4-7': {
    apiName: 'claude-opus-4-7',
    llmName: 'claude-opus-4-7',
    displayName: 'Claude Opus 4.7',
    provider: 'Anthropic',
    category: 'text',
    supportsVision: true,
    contextWindow: 200000,
    description: 'Anthropic\'s Claude Opus 4.7 via Vertex AI (new tokenizer, ~35% more tokens)',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 4, speedRating: 2, intelligenceRating: 5,
  },
  'claude-opus-4-8': {
    apiName: 'claude-opus-4-8',
    llmName: 'claude-opus-4-8',
    displayName: 'Claude Opus 4.8',
    provider: 'Anthropic',
    category: 'text',
    supportsVision: true,
    contextWindow: 200000,
    description: 'Anthropic\'s Claude Opus 4.8 via Vertex AI',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 4, speedRating: 2, intelligenceRating: 5,
  },
  'claude-opus-5': {
    apiName: 'claude-opus-5',
    llmName: 'claude-opus-5',
    displayName: 'Claude Opus 5',
    provider: 'Anthropic',
    category: 'text',
    supportsVision: true,
    contextWindow: 200000,
    description: 'Anthropic\'s Claude Opus 5 via Vertex AI',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 4, speedRating: 2, intelligenceRating: 5,
  },
  'claude-sonnet-5-5': {
    apiName: 'claude-sonnet-5-5',
    llmName: 'claude-sonnet-5-5',
    displayName: 'Claude Sonnet 5.5',
    provider: 'Anthropic',
    category: 'text',
    supportsVision: true,
    contextWindow: 1000000,
    description: 'Anthropic\'s Claude Sonnet 5.5 — latest Sonnet, via Vertex AI',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 3, speedRating: 4, intelligenceRating: 5,
  },
  'claude-opus-5-5': {
    apiName: 'claude-opus-5-5',
    llmName: 'claude-opus-5-5',
    displayName: 'Claude Opus 5.5',
    provider: 'Anthropic',
    category: 'text',
    supportsVision: true,
    contextWindow: 1000000,
    description: 'Anthropic\'s Claude Opus 5.5 — latest Opus, via Vertex AI',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 4, speedRating: 2, intelligenceRating: 5,
  },
  'claude-fable-5-1': {
    apiName: 'claude-fable-5-1',
    llmName: 'claude-fable-5-1',
    displayName: 'Claude Fable 5.1',
    provider: 'Anthropic',
    category: 'text',
    supportsVision: true,
    contextWindow: 1000000,
    description: 'Anthropic\'s Claude Fable 5.1 — top-tier reasoning, via Vertex AI',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 5, speedRating: 1, intelligenceRating: 5,
  },
  
  // Meta Llama Models (via Vertex AI Model Garden MaaS)
  // Hidden until MaaS is enabled in Model Garden (accept Meta license on the -maas model cards)
  'llama4-scout': {
    apiName: 'llama4-scout',
    llmName: 'meta/llama-4-scout-17b-16e-instruct-maas',
    displayName: 'Llama 4 Scout',
    provider: 'Meta',
    category: 'text',
    supportsVision: true,
    contextWindow: 10000000,
    description: 'Meta Llama 4 Scout — MoE 109B, 10M context via Vertex AI',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 4, intelligenceRating: 3,
  },
  'llama4-maverick': {
    apiName: 'llama4-maverick',
    llmName: 'meta/llama-4-maverick-17b-128e-instruct-maas',
    displayName: 'Llama 4 Maverick',
    provider: 'Meta',
    category: 'text',
    supportsVision: true,
    contextWindow: 1048576,
    description: 'Meta Llama 4 Maverick — MoE 400B, most capable Llama via Vertex AI',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 3, intelligenceRating: 4,
  },
  'llama3-3-70b': {
    apiName: 'llama3-3-70b',
    llmName: 'meta/llama-3.3-70b-instruct-maas',
    displayName: 'Llama 3.3 70B',
    provider: 'Meta',
    category: 'text',
    supportsVision: false,
    contextWindow: 128000,
    description: 'Meta Llama 3.3 70B — text-only, high quality via Vertex AI',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 2, speedRating: 3, intelligenceRating: 3,
  },
  
  // Mistral Models (via Vertex AI Model Garden MaaS)
  // Hidden until MaaS is enabled in Model Garden (accept Mistral terms on the MaaS model cards)
  'mistral-medium-3': {
    apiName: 'mistral-medium-3',
    llmName: 'mistralai/mistral-medium-3@latest',
    displayName: 'Mistral Medium 3',
    provider: 'Mistral',
    category: 'text',
    supportsVision: false,
    contextWindow: 131072,
    description: 'Mistral Medium 3 — versatile, reasoning and coding via Vertex AI',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 3, intelligenceRating: 4,
  },
  'mistral-small-3-1': {
    apiName: 'mistral-small-3-1',
    llmName: 'mistralai/mistral-small-2503@latest',
    displayName: 'Mistral Small 3.1',
    provider: 'Mistral',
    category: 'text',
    supportsVision: false,
    contextWindow: 131072,
    description: 'Mistral Small 3.1 — cheapest MaaS model available via Vertex AI',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 5, intelligenceRating: 2,
  },
  'codestral-2': {
    apiName: 'codestral-2',
    llmName: 'mistralai/codestral-2@latest',
    displayName: 'Codestral 2',
    provider: 'Mistral',
    category: 'text',
    supportsVision: false,
    contextWindow: 262144,
    description: 'Mistral Codestral 2 — code generation specialist via Vertex AI',
    isAvailable: false,
    supportsFunctionCalling: true,
    costRating: 1, speedRating: 4, intelligenceRating: 3,
  },
  
  // DeepSeek Models (via Vertex AI Model Garden MaaS)
  'deepseek-v3-2': {
    apiName: 'deepseek-v3-2',
    llmName: 'deepseek-ai/deepseek-v3.2-maas',
    displayName: 'DeepSeek V3.2',
    provider: 'DeepSeek',
    category: 'text',
    supportsVision: false,
    contextWindow: 131072,
    description: 'DeepSeek V3.2 — strong general reasoning via Vertex AI',
    isAvailable: true,
    supportsFunctionCalling: true,
    costRating: 2, speedRating: 3, intelligenceRating: 4,
  },
  'deepseek-r1': {
    apiName: 'deepseek-r1',
    llmName: 'deepseek-ai/deepseek-r1-0528-maas',
    displayName: 'DeepSeek R1',
    provider: 'DeepSeek',
    category: 'text',
    supportsVision: false,
    contextWindow: 131072,
    description: 'DeepSeek R1 — reasoning specialist via Vertex AI',
    isAvailable: true,
    supportsFunctionCalling: false,
    costRating: 3, speedRating: 2, intelligenceRating: 5,
  },
  
  // Embedding Models
  'openai-large': {
    apiName: 'openai-large',
    llmName: 'text-embedding-3-large',
    displayName: 'OpenAI Embeddings Large',
    provider: 'OpenAI',
    category: 'embedding',
    supportsFunctionCalling: false,
    description: 'High-quality embeddings',
    isAvailable: true,
    costRating: 2, speedRating: 3, intelligenceRating: 4,
  },
  'openai-small': {
    apiName: 'openai-small',
    llmName: 'text-embedding-3-small',
    displayName: 'OpenAI Embeddings Small',
    provider: 'OpenAI',
    category: 'embedding',
    supportsFunctionCalling: false,
    description: 'Fast, efficient embeddings',
    isAvailable: true,
    costRating: 1, speedRating: 5, intelligenceRating: 3,
  },
  'gemini-embeddings': {
    apiName: 'gemini-embeddings',
    llmName: 'textembedding-gecko',
    displayName: 'Gemini Embeddings',
    provider: 'Google',
    category: 'embedding',
    supportsFunctionCalling: false,
    description: 'Google\'s embedding model',
    isAvailable: true,
    costRating: 1, speedRating: 4, intelligenceRating: 3,
  },
  
  // Image Generation Models
  // OpenAI Image Generation
  'dall-e-2': {
    apiName: 'dall-e-2',
    llmName: 'dall-e-2',
    displayName: 'DALL-E 2 (deprecated)',
    provider: 'OpenAI',
    category: 'image',
    supportsFunctionCalling: false,
    description: 'Retired 2026-05-12 — auto-remapped to Nano Banana 2 Lite',
    isAvailable: false,
    costRating: 2, speedRating: 4, intelligenceRating: 2,
  },
  'dall-e-3': {
    apiName: 'dall-e-3',
    llmName: 'dall-e-3',
    displayName: 'DALL-E 3 (deprecated)',
    provider: 'OpenAI',
    category: 'image',
    supportsFunctionCalling: false,
    description: 'Retired 2026-05-12 — auto-remapped to GPT Image 2',
    isAvailable: false,
    costRating: 3, speedRating: 3, intelligenceRating: 3,
  },
  'gpt-image-1': {
    apiName: 'gpt-image-1',
    llmName: 'gpt-image-1',
    displayName: 'GPT Image 1 (deprecated)',
    provider: 'OpenAI',
    category: 'image',
    supportsFunctionCalling: false,
    description: 'Deprecated 2026-10-23 — auto-remapped to GPT Image 2',
    isAvailable: false,
    costRating: 3, speedRating: 3, intelligenceRating: 4,
  },
  'gpt-image-1.5': {
    apiName: 'gpt-image-1.5',
    llmName: 'gpt-image-1.5',
    displayName: 'GPT Image 1.5 (deprecated)',
    provider: 'OpenAI',
    category: 'image',
    supportsFunctionCalling: false,
    description: 'Deprecated 2026-12-01 — auto-remapped to GPT Image 2',
    isAvailable: false,
    costRating: 3, speedRating: 3, intelligenceRating: 5,
  },
  'gpt-image-1-mini': {
    apiName: 'gpt-image-1-mini',
    llmName: 'gpt-image-1-mini',
    displayName: 'GPT Image 1 Mini (deprecated)',
    provider: 'OpenAI',
    category: 'image',
    supportsFunctionCalling: false,
    description: 'Deprecated 2026-12-01 — auto-remapped to Nano Banana 2 Lite',
    isAvailable: false,
    costRating: 1, speedRating: 5, intelligenceRating: 3,
  },
  'gpt-image-2': {
    apiName: 'gpt-image-2',
    llmName: 'gpt-image-2',
    displayName: 'GPT Image 2',
    provider: 'OpenAI',
    category: 'image',
    supportsFunctionCalling: false,
    description: 'Latest OpenAI image generation — replaces DALL-E and GPT Image 1.x',
    isAvailable: true,
    costRating: 3, speedRating: 3, intelligenceRating: 5,
  },
  
  // Google Imagen Models
  'imagen-4.0-generate-001': {
    apiName: 'imagen-4.0-generate-001',
    llmName: 'imagen-4.0-generate-001',
    displayName: 'Imagen 4.0 (deprecated)',
    provider: 'Google',
    category: 'image',
    supportsFunctionCalling: false,
    description: 'Retired 2026-06-30 — auto-remapped to Nano Banana 2',
    isAvailable: false,
    costRating: 2, speedRating: 3, intelligenceRating: 4,
  },
  'imagen-4.0-fast-generate-001': {
    apiName: 'imagen-4.0-fast-generate-001',
    llmName: 'imagen-4.0-fast-generate-001',
    displayName: 'Imagen 4.0 Fast (deprecated)',
    provider: 'Google',
    category: 'image',
    supportsFunctionCalling: false,
    description: 'Retired 2026-06-30 — auto-remapped to Nano Banana 2 Lite',
    isAvailable: false,
    costRating: 1, speedRating: 5, intelligenceRating: 3,
  },
  'imagen-4.0-ultra-generate-001': {
    apiName: 'imagen-4.0-ultra-generate-001',
    llmName: 'imagen-4.0-ultra-generate-001',
    displayName: 'Imagen 4.0 Ultra (deprecated)',
    provider: 'Google',
    category: 'image',
    supportsFunctionCalling: false,
    description: 'Retired 2026-06-30 — auto-remapped to Nano Banana Pro',
    isAvailable: false,
    costRating: 4, speedRating: 2, intelligenceRating: 5,
  },
  
  // Gemini Image Generation (working)
  'gemini-image-generation': {
    apiName: 'gemini-image-generation',
    llmName: 'gemini-2.5-flash-image',
    displayName: 'Gemini Image (deprecated)',
    provider: 'Google',
    category: 'image',
    supportsFunctionCalling: false,
    description: 'Deprecated 2026-10-02 — auto-remapped to Nano Banana 2 Lite',
    isAvailable: false,
    costRating: 2, speedRating: 4, intelligenceRating: 3,
  },
  'nano-banana-2': {
    apiName: 'nano-banana-2',
    llmName: 'gemini-3.1-flash-image',
    displayName: 'Nano Banana 2',
    provider: 'Google',
    category: 'image',
    supportsFunctionCalling: false,
    description: 'Fast image gen — text rendering, character consistency, up to 4K (GA)',
    isAvailable: true,
    costRating: 2, speedRating: 5, intelligenceRating: 5,
  },
  'nano-banana-2-lite': {
    apiName: 'nano-banana-2-lite',
    llmName: 'gemini-3.1-flash-lite-image',
    displayName: 'Nano Banana 2 Lite',
    provider: 'Google',
    category: 'image',
    supportsFunctionCalling: false,
    description: 'High-volume, latency-sensitive image gen (GA)',
    isAvailable: true,
    costRating: 1, speedRating: 5, intelligenceRating: 4,
  },
  'nano-banana-pro': {
    apiName: 'nano-banana-pro',
    llmName: 'gemini-3-pro-image',
    displayName: 'Nano Banana Pro',
    provider: 'Google',
    category: 'image',
    supportsFunctionCalling: false,
    description: 'Professional-grade 4K image gen with complex layouts and high-fidelity text (GA)',
    isAvailable: true,
    costRating: 4, speedRating: 2, intelligenceRating: 5,
  },
  
  // Video Generation Models - Gen AI API only (Vertex AI veo-3.1 needs permissions)
  'veo-3.0': {
    apiName: 'veo-3.0',
    llmName: 'veo-3.0-generate-001',
    displayName: 'Veo 3.0 (deprecated)',
    provider: 'Google',
    category: 'video' as any,
    supportsFunctionCalling: false,
    description: 'Retired 2026-06-30 — auto-remapped to Veo 3.1',
    isAvailable: false,
    costRating: 4, speedRating: 2, intelligenceRating: 4,
  },
  'veo-2.0': {
    apiName: 'veo-2.0',
    llmName: 'veo-2.0-generate-001',
    displayName: 'Veo 2.0 (deprecated)',
    provider: 'Google',
    category: 'video' as any,
    supportsFunctionCalling: false,
    description: 'Retired 2026-06-30 — auto-remapped to Veo 3.1',
    isAvailable: false,
    costRating: 2, speedRating: 3, intelligenceRating: 2,
  },
  'veo-3.0-fast': {
    apiName: 'veo-3.0-fast',
    llmName: 'veo-3.0-fast-generate-001',
    displayName: 'Veo 3.0 Fast (deprecated)',
    provider: 'Google',
    category: 'video' as any,
    supportsFunctionCalling: false,
    description: 'Retired 2026-06-30 — auto-remapped to Veo 3.1 Fast',
    isAvailable: false,
    costRating: 2, speedRating: 4, intelligenceRating: 3,
  },
  'veo-3.1': {
    apiName: 'veo-3.1',
    llmName: 'veo-3.1-generate-001',
    displayName: 'Veo 3.1',
    provider: 'Google',
    category: 'video' as any,
    supportsFunctionCalling: false,
    description: 'Best quality video generation',
    isAvailable: true,
    costRating: 5, speedRating: 2, intelligenceRating: 5,
  },
  'veo-3.1-fast': {
    apiName: 'veo-3.1-fast',
    llmName: 'veo-3.1-fast-generate-001',
    displayName: 'Veo 3.1 Fast',
    provider: 'Google',
    category: 'video' as any,
    supportsFunctionCalling: false,
    description: 'Faster video generation, slightly lower quality',
    isAvailable: true,
    costRating: 3, speedRating: 4, intelligenceRating: 4,
  },
  // Back-compat aliases for long-form Vertex keys stored in DB — hidden from UI
  'veo-3.0-generate-001': {
    apiName: 'veo-3.0-generate-001',
    llmName: 'veo-3.0-generate-001',
    displayName: 'Veo 3.0',
    provider: 'Google',
    category: 'video' as any,
    supportsFunctionCalling: false,
    description: 'Vertex AI Veo 3.0',
    isAvailable: false,
  },
  'veo-3.0-fast-generate-001': {
    apiName: 'veo-3.0-fast-generate-001',
    llmName: 'veo-3.0-fast-generate-001',
    displayName: 'Veo 3.0 Fast',
    provider: 'Google',
    category: 'video' as any,
    supportsFunctionCalling: false,
    description: 'Vertex AI Veo 3.0 Fast',
    isAvailable: false,
  },
  'veo-3.1-generate-001': {
    apiName: 'veo-3.1-generate-001',
    llmName: 'veo-3.1-generate-001',
    displayName: 'Veo 3.1',
    provider: 'Google',
    category: 'video' as any,
    supportsFunctionCalling: false,
    description: 'Vertex AI Veo 3.1',
    isAvailable: false,
  },
  'veo-3.1-fast-generate-001': {
    apiName: 'veo-3.1-fast-generate-001',
    llmName: 'veo-3.1-fast-generate-001',
    displayName: 'Veo 3.1 Fast',
    provider: 'Google',
    category: 'video' as any,
    supportsFunctionCalling: false,
    description: 'Vertex AI Veo 3.1 Fast',
    isAvailable: false,
  },
  // Future: OpenAI Sora (when API key available)
  // 'sora': { ... },
  
  // Audio Models
  'whisper-large-v3': {
    apiName: 'whisper-large-v3',
    llmName: 'whisper-large-v3',
    displayName: 'Whisper Large V3',
    provider: 'OpenAI',
    category: 'audio',
    supportsFunctionCalling: false,
    description: 'Advanced speech-to-text transcription model. Supports 50+ languages with automatic detection. Best for meetings, voice memos, podcasts, and multilingual content.',
    isAvailable: true,
    costRating: 2, speedRating: 4, intelligenceRating: 4,
  },
};

interface ModelVariant {
  base: string;
  label: string;
  description: string;
  ratings: Pick<ModelInfo, 'costRating' | 'speedRating' | 'intelligenceRating'>;
}

// Presets of a base model with fixed request settings. Mirrors MODEL_VARIANTS in
// api/common/model_config.py, which holds the settings themselves.
export const MODEL_VARIANTS: Readonly<Record<string, ModelVariant>> = {
  'gpt-6-luna-low': {
    base: 'gpt-6-luna',
    label: 'low-detail images',
    description: 'GPT-6 Luna with images at low detail (512px) — about 10x cheaper per image than GPT-5.4 Nano; may miss small items and confuse similar-looking foods',
    ratings: { costRating: 1, speedRating: 4, intelligenceRating: 3 },
  },
  'gpt-6-sol-fast': {
    base: 'gpt-6-sol',
    label: 'fast',
    description: 'GPT-6 Sol with reasoning off — about 40% faster; for extraction and formatting rather than multi-step reasoning',
    ratings: { costRating: 3, speedRating: 5, intelligenceRating: 4 },
  },
  'gemini-3-5-flash-fast': {
    base: 'gemini-3-5-flash',
    label: 'fast',
    description: 'Gemini 3.5 Flash with thinking off — about 5x faster and ~1,000 fewer billed output tokens per call; for extraction rather than multi-step reasoning',
    ratings: { costRating: 2, speedRating: 5, intelligenceRating: 4 },
  },
};

for (const [tag, variant] of Object.entries(MODEL_VARIANTS)) {
  const base = MODEL_REGISTRY[variant.base];
  MODEL_REGISTRY[tag] = {
    ...base,
    ...variant.ratings,
    apiName: tag,
    displayName: `${base.displayName} (${variant.label})`,
    description: variant.description,
    variantOf: variant.base,
  };
}

/** The base model of a variant tag; any other tag is returned unchanged. */
export function variantBase(apiName: string): string {
  return MODEL_VARIANTS[apiName]?.base ?? apiName;
}

// Default models for new AI functions
export const DEFAULT_ALLOWED_MODELS = [
  'gemini-3-5-flash-lite',  // First one is the default selection - successor to 2.5 Flash
  'gemini-3-8-flash',
  'gemini-3-7-flash',
  'gemini-3-5-flash',
  'gemini-3-5-flash-fast',
  'gemini-3-1-flash-lite',
  'gemini-3-1-pro-preview',
  'gemini-3-flash-preview',
  'gemini-3-6-flash',
  'gemini-3-flash',
  'gemma-4',
  'gpt-6.1-sol',
  'gpt-6-sol',
  'gpt-6-sol-fast',
  'gpt-6-luna',
  'gpt-6-luna-low',
  'gpt-6-astra',
  'gpt-5.6-sol',
  'gpt-5.6-terra',
  'gpt-5.6-luna',
  'gpt-5.5',
  'gpt-5.5-pro',
  'gpt-5.4',
  'gpt-5.4-pro',
  'gpt-5.3',
  'gpt-5.4-mini',
  'gpt-5.2',
  'gpt-4.1',
  'gpt-4.1-mini',
  'gpt-4o',
  'gpt-4o-mini',
  'grok4-7',
  'grok4-6',
  'grok4-5',
  'grok4-3',
  'grok4-1-fast',
  'grok4-20',
  'claude-sonnet-5-5',
  'claude-opus-5-5',
  'claude-fable-5-1',
  'claude-sonnet-5',
  'claude-opus-5',
  'claude-opus-4-8',
  'claude-opus-4-7',
  'claude-sonnet-4-6',
  'claude-haiku-4-5',
  'claude-opus-4-5',
  'claude-opus-4-6',
  'deepseek-v3-2',
  'deepseek-r1'
];

export const DEFAULT_ALLOWED_MODEL_FOR_PUBLIC_FUNCTIONS = [
  'gemini-3-5-flash-lite',  // First one is the default selection - successor to 2.5 Flash
  'gemini-3-8-flash',
  'gemini-3-7-flash',
  'gemini-3-5-flash',
  'gemini-3-5-flash-fast',
  'gemini-3-1-flash-lite',
  'gemini-3-1-pro-preview',
  'gemini-3-flash-preview',
  'gemini-3-6-flash',
  'gemini-3-flash',
  'gemma-4',
  'gpt-6.1-sol',
  'gpt-6-sol',
  'gpt-6-sol-fast',
  'gpt-6-luna',
  'gpt-6-luna-low',
  'gpt-6-astra',
  'gpt-5.6-sol',
  'gpt-5.6-terra',
  'gpt-5.6-luna',
  'gpt-5.5',
  'gpt-5.5-pro',
  'gpt-5.4',
  'gpt-5.4-pro',
  'gpt-5.3',
  'gpt-5.4-mini',
  'gpt-5.2',
  'gpt-4.1',
  'gpt-4.1-mini',
  'gpt-4o',
  'gpt-4o-mini',
  'grok4-7',
  'grok4-6',
  'grok4-5',
  'grok4-3',
  'grok4-1-fast',
  'grok4-20',
  'claude-sonnet-5-5',
  'claude-opus-5-5',
  'claude-fable-5-1',
  'claude-sonnet-5',
  'claude-opus-5',
  'claude-opus-4-8',
  'claude-opus-4-7',
  'claude-sonnet-4-6',
  'claude-haiku-4-5',
  'claude-opus-4-5',
  'claude-opus-4-6',
  'deepseek-v3-2',
  'deepseek-r1'
];


// ----------------------------------------------------------------------
// Zero Data Retention (ZDR / Privacy / "no-train" mode)
// ----------------------------------------------------------------------
//
// MUST mirror `ZDR_ELIGIBLE_MODELS` in api/common/model_config.py. This
// is the US-residency set: models that route through Vertex AI under our
// no-train contract AND can be pinned to a US-only region (no `global`
// multi-region endpoint). In the `fastest` region mode, global-only
// Vertex models and OpenAI-direct GPT models (store=False) are also
// allowed — see `isZDREligibleForRegion`.
//
// Keep this list authoritative — the LLM service does its own check
// before serving a request, but the frontend uses this for cheap
// dropdown filtering and to render the "Privacy mode is ON" banner.
const ZDR_ELIGIBLE_MODELS: ReadonlySet<string> = new Set([
  // Gemini text — Vertex AI
  'gemini-2-5-flash',
  'gemini-2-5-pro',
  'gemini-2-5-flash-lite',
  'gemini-3-flash-preview',
  'gemini-3-1-pro-preview',
  'gemini-3-1-flash-lite',
  'gemini-3-5-flash',
  'gemini-3-5-flash-lite',
  'gemini-3-flash',
  'gemini-3-6-flash',
  'gemini-3-7-flash',
  'gemini-3-8-flash',
  // Gemma — Vertex AI Model Garden MaaS
  'gemma-4',
  // Anthropic Claude — Vertex AI Model Garden (us-east5, or the `us`
  // multi-region for Opus 4.7+ / Sonnet 5+ / Fable)
  'claude-sonnet-4',
  'claude-haiku-4',
  'claude-opus-4',
  'claude-sonnet-4-5',
  'claude-sonnet-4-6',
  'claude-opus-4-1',
  'claude-opus-4-5',
  'claude-opus-4-6',
  'claude-haiku-4-5',
  'claude-sonnet-5',
  'claude-opus-4-7',
  'claude-opus-4-8',
  'claude-opus-5',
  'claude-sonnet-5-5',
  'claude-opus-5-5',
  // Not Claude Fable 5.1: Vertex serves it only with Anthropic data sharing on.
  // Meta Llama — Vertex AI Model Garden MaaS
  'llama4-scout',
  'llama4-maverick',
  'llama3-3-70b',
  // Mistral — Vertex AI Model Garden MaaS
  'mistral-medium-3',
  'mistral-small-3-1',
  'codestral-2',
  // DeepSeek — Vertex AI Model Garden MaaS
  'deepseek-v3-2',
  'deepseek-r1',
  // Image generation — Vertex AI
  'imagen-4.0-generate-001',
  'imagen-4.0-fast-generate-001',
  'imagen-4.0-ultra-generate-001',
  'gemini-image-generation',
  'nano-banana-2',
  'nano-banana-2-lite',
  'nano-banana-pro',
]);

// Models that ONLY have a `global` Vertex endpoint (no US deployment) —
// must be skipped under ZDR even if requested. Today this is just the
// deprecated 3 Pro Preview.
const ZDR_GLOBAL_ONLY_MODELS: ReadonlySet<string> = new Set([
  'gemini-3-pro-preview',
  'gemma-4',
  'deepseek-v3-2',
]);

/** True iff the given model can be served under Zero Data Retention with US residency. */
export function isZDREligible(apiName: string | null | undefined): boolean {
  if (!apiName) return false;
  const base = variantBase(apiName);
  return ZDR_ELIGIBLE_MODELS.has(base) && !ZDR_GLOBAL_ONLY_MODELS.has(base);
}

// Mirrors `OPENAI_ZDR_ELIGIBLE_MODELS` in api/common/model_config.py. The
// backend can switch these off (OPENAI_ZDR_ENABLED); it stays authoritative.
function isOpenAINoTrain(apiName: string): boolean {
  const info = MODEL_REGISTRY[apiName];
  return !!info && info.provider === 'OpenAI' && info.category === 'text' && apiName !== 'gpt35t';
}

/**
 * Region-aware ZDR eligibility. `us` → US-pinned Vertex models only;
 * `fastest` → also global-only Vertex models and OpenAI-direct GPT.
 */
export function isZDREligibleForRegion(
  apiName: string | null | undefined,
  regionMode: string | null | undefined,
): boolean {
  if (!apiName) return false;
  if (regionMode === 'us') return isZDREligible(apiName);
  const base = variantBase(apiName);
  return ZDR_ELIGIBLE_MODELS.has(base) || ZDR_GLOBAL_ONLY_MODELS.has(base) || isOpenAINoTrain(base);
}

// ----------------------------------------------------------------------
// Vertex AI / OpenAI / xAI deprecation registry
// ----------------------------------------------------------------------
//
// MUST mirror `DEPRECATED_MODELS` in `api/common/model_config.py`. A new
// provider sunset = one entry here AND there. The Python script at
// `scripts/check-deprecation-sync.py` fails CI on drift.
//
// At request time the LLM service rewrites any deprecated tag to its
// replacement and emits an `X-Model-Deprecated` response header. The
// frontend uses this map to hide the tag from selectors and surface a
// "this model is going away, we're auto-routing to X" notice for
// existing functions still configured with it.
export interface DeprecationInfo {
  /** Default replacement (text / JSON tools). */
  replacement: string;
  /** Optional per-tool override for image-class routes. Empty == use replacement. */
  imageReplacement?: string;
  /** YYYY-MM-DD upstream retirement date, surfaced in headers. */
  sunsetDate: string;
  /** Human-readable explanation, logged and surfaced in headers. */
  reason: string;
}

export const DEPRECATED_MODELS: Readonly<Record<string, DeprecationInfo>> = {
  'gemini-2': {
    replacement: 'gemini-3-1-flash-lite',
    sunsetDate: '2026-06-01',
    reason:
      "Vertex AI is retiring Gemini 2.0 Flash on 2026-06-01. Switching to " +
      "Gemini 3.1 Flash Lite (GA, same speed/cost, better quality).",
  },
  'gemini': {
    replacement: 'gemini-3-1-flash-lite',
    sunsetDate: '2025-09-24',
    reason:
      "Gemini 1.5 Flash family retired by Google in late 2025. Routing to " +
      "Gemini 3.1 Flash Lite.",
  },
  'gemini-pro': {
    replacement: 'gemini-3-5-flash',
    sunsetDate: '2025-09-24',
    reason:
      "Gemini 1.5 Pro retired by Google in late 2025. Routing to Gemini 3.5 Flash.",
  },
  'gemini-experimental': {
    replacement: 'gemini-3-5-flash',
    sunsetDate: '2025-09-24',
    reason:
      "gemini-experimental was an alias for gemini-1.5-flash-8b-exp, retired " +
      "alongside the rest of the 1.5 family.",
  },
  'gemini-2-5-flash-lite': {
    replacement: 'gemini-3-1-flash-lite',
    sunsetDate: '2026-10-20',
    reason:
      "Vertex AI is retiring Gemini 2.5 Flash-Lite on 2026-10-20. Switching to " +
      "Gemini 3.1 Flash Lite (GA, official replacement).",
  },
  'gemini-2-5-flash': {
    replacement: 'gemini-3-5-flash-lite',
    sunsetDate: '2026-10-20',
    reason:
      "Vertex AI is retiring Gemini 2.5 Flash on 2026-10-20. Switching to " +
      "Gemini 3.5 Flash-Lite (GA, same $0.30/$2.50 tier).",
  },
  'gemini-2-5-pro': {
    replacement: 'gemini-3-5-flash',
    sunsetDate: '2026-10-20',
    reason:
      "Vertex AI is retiring Gemini 2.5 Pro on 2026-10-20. Switching to " +
      "Gemini 3.5 Flash (GA, official replacement).",
  },
  'gemini-image-generation': {
    replacement: 'nano-banana-2-lite',
    imageReplacement: 'nano-banana-2-lite',
    sunsetDate: '2026-10-02',
    reason:
      "Vertex AI is retiring Gemini 2.5 Flash Image on 2026-10-02. Switching to " +
      "Nano Banana 2 Lite (Gemini 3.1 Flash-Lite Image).",
  },
  'imagen-4.0-generate-001': {
    replacement: 'nano-banana-2',
    imageReplacement: 'nano-banana-2',
    sunsetDate: '2026-06-30',
    reason: "Vertex AI discontinued Imagen 4 on 2026-06-30. Switching to Nano Banana 2.",
  },
  'imagen-4.0-fast-generate-001': {
    replacement: 'nano-banana-2-lite',
    imageReplacement: 'nano-banana-2-lite',
    sunsetDate: '2026-06-30',
    reason: "Vertex AI discontinued Imagen 4 Fast on 2026-06-30. Switching to Nano Banana 2 Lite.",
  },
  'imagen-4.0-ultra-generate-001': {
    replacement: 'nano-banana-pro',
    imageReplacement: 'nano-banana-pro',
    sunsetDate: '2026-06-30',
    reason: "Vertex AI discontinued Imagen 4 Ultra on 2026-06-30. Switching to Nano Banana Pro.",
  },
  'veo-3.0': {
    replacement: 'veo-3.1',
    sunsetDate: '2026-06-30',
    reason: "Vertex AI retired Veo 3.0 on 2026-06-30. Switching to Veo 3.1.",
  },
  'veo-3.0-generate-001': {
    replacement: 'veo-3.1',
    sunsetDate: '2026-06-30',
    reason: "Vertex AI retired Veo 3.0 on 2026-06-30. Switching to Veo 3.1.",
  },
  'veo-3.0-fast': {
    replacement: 'veo-3.1-fast',
    sunsetDate: '2026-06-30',
    reason: "Vertex AI retired Veo 3.0 Fast on 2026-06-30. Switching to Veo 3.1 Fast.",
  },
  'veo-3.0-fast-generate-001': {
    replacement: 'veo-3.1-fast',
    sunsetDate: '2026-06-30',
    reason: "Vertex AI retired Veo 3.0 Fast on 2026-06-30. Switching to Veo 3.1 Fast.",
  },
  'veo-2.0': {
    replacement: 'veo-3.1',
    sunsetDate: '2026-06-30',
    reason: "Vertex AI retired Veo 2.0 on 2026-06-30. Switching to Veo 3.1.",
  },
  'veo-2.0-generate-001': {
    replacement: 'veo-3.1',
    sunsetDate: '2026-06-30',
    reason: "Vertex AI retired Veo 2.0 on 2026-06-30. Switching to Veo 3.1.",
  },
  'gpt35t': {
    replacement: 'gpt-6-luna',
    sunsetDate: '2026-10-23',
    reason: "OpenAI is shutting down gpt-3.5-turbo on 2026-10-23. Switching to GPT-6 Luna.",
  },
  'gpt-4.1-nano': {
    replacement: 'gpt-6-luna',
    sunsetDate: '2026-10-23',
    reason: "OpenAI is shutting down gpt-4.1-nano on 2026-10-23. Switching to GPT-6 Luna.",
  },
  'o1-mini': {
    replacement: 'gpt-5.4-mini',
    sunsetDate: '2025-10-27',
    reason: "OpenAI shut down o1-mini on 2025-10-27. Switching to GPT-5.4 Mini.",
  },
  'o3-mini': {
    replacement: 'gpt-5.4-mini',
    sunsetDate: '2026-10-23',
    reason: "OpenAI is shutting down o3-mini on 2026-10-23. Switching to GPT-5.4 Mini.",
  },
  'o4-mini': {
    replacement: 'gpt-5.4-mini',
    sunsetDate: '2026-10-23',
    reason: "OpenAI is shutting down o4-mini on 2026-10-23. Switching to GPT-5.4 Mini.",
  },
  'gpt-5': {
    replacement: 'gpt-6-sol',
    sunsetDate: '2026-12-11',
    reason: "OpenAI is shutting down gpt-5 on 2026-12-11. Switching to GPT-6 Sol.",
  },
  'gpt-5-pro': {
    replacement: 'gpt-6-astra',
    sunsetDate: '2026-12-11',
    reason: "OpenAI is shutting down gpt-5-pro on 2026-12-11. Switching to GPT-6 Astra.",
  },
  'gpt-5-mini': {
    replacement: 'gpt-6-luna',
    sunsetDate: '2026-12-11',
    reason: "OpenAI is shutting down gpt-5-mini on 2026-12-11. Switching to GPT-6 Luna.",
  },
  'gpt-5-nano': {
    replacement: 'gpt-6-luna',
    sunsetDate: '2026-12-11',
    reason: "OpenAI is shutting down gpt-5-nano on 2026-12-11. Switching to GPT-6 Luna.",
  },
  'gpt-5.1': {
    replacement: 'gpt-6-sol',
    sunsetDate: '2027-04-01',
    reason: "OpenAI is shutting down gpt-5.1 on 2027-04-01. Switching to GPT-6 Sol.",
  },
  'gpt-5.4-nano': {
    replacement: 'gpt-6-luna',
    imageReplacement: 'gpt-5.4-nano',
    sunsetDate: '2027-04-01',
    reason: "OpenAI is shutting down gpt-5.4-nano on 2027-04-01. Switching to GPT-6 Luna for text; images stay on gpt-5.4-nano until then.",
  },
  'dall-e-2': {
    replacement: 'nano-banana-2-lite',
    imageReplacement: 'nano-banana-2-lite',
    sunsetDate: '2026-05-12',
    reason: "OpenAI shut down DALL-E 2 on 2026-05-12. Switching to Nano Banana 2 Lite.",
  },
  'dall-e-3': {
    replacement: 'gpt-image-2',
    imageReplacement: 'gpt-image-2',
    sunsetDate: '2026-05-12',
    reason: "OpenAI shut down DALL-E 3 on 2026-05-12. Switching to GPT Image 2.",
  },
  'gpt-image-1': {
    replacement: 'gpt-image-2',
    imageReplacement: 'gpt-image-2',
    sunsetDate: '2026-10-23',
    reason: "OpenAI is shutting down gpt-image-1 on 2026-10-23. Switching to GPT Image 2.",
  },
  'gpt-image-1.5': {
    replacement: 'gpt-image-2',
    imageReplacement: 'gpt-image-2',
    sunsetDate: '2026-12-01',
    reason: "OpenAI is shutting down gpt-image-1.5 on 2026-12-01. Switching to GPT Image 2.",
  },
  'gpt-image-1-mini': {
    replacement: 'nano-banana-2-lite',
    imageReplacement: 'nano-banana-2-lite',
    sunsetDate: '2026-12-01',
    reason: "OpenAI is shutting down gpt-image-1-mini on 2026-12-01. Switching to Nano Banana 2 Lite.",
  },
  'claude-sonnet-4': {
    replacement: 'claude-sonnet-4-6',
    sunsetDate: '2026-09-14',
    reason: "Vertex AI retired Claude Sonnet 4 on 2026-09-14. Switching to Claude Sonnet 4.6.",
  },
  'claude-opus-4': {
    replacement: 'claude-opus-4-8',
    sunsetDate: '2026-09-14',
    reason: "Vertex AI retired Claude Opus 4 on 2026-09-14. Switching to Claude Opus 4.8.",
  },
  'claude-opus-4-1': {
    replacement: 'claude-opus-4-8',
    sunsetDate: '2026-08-05',
    reason: "Anthropic retired Claude Opus 4.1 on 2026-08-05. Switching to Claude Opus 4.8.",
  },
  'claude-sonnet-4-5': {
    replacement: 'claude-sonnet-5-5',
    sunsetDate: '2026-11-30',
    reason: "Anthropic is retiring Claude Sonnet 4.5 on 2026-11-30. Switching to Claude Sonnet 5.5.",
  },
  'claude-haiku-4': {
    replacement: 'claude-haiku-4-5',
    sunsetDate: '2025-10-15',
    reason: "There is no Claude Haiku 4 on Vertex AI. Switching to Claude Haiku 4.5.",
  },
  'grok2': {
    replacement: 'grok4-3',
    sunsetDate: '2026-01-15',
    reason: "xAI removed Grok 2 in January 2026. Switching to Grok 4.3.",
  },
  'grok3': {
    replacement: 'grok4-3',
    sunsetDate: '2026-05-15',
    reason: "xAI retired Grok 3 on 2026-05-15. Switching to Grok 4.3.",
  },
  'grok3-mini': {
    replacement: 'gpt-6-luna',
    sunsetDate: '2026-05-15',
    reason: "xAI retired Grok 3 Mini on 2026-05-15. Switching to GPT-6 Luna (closest price and speed).",
  },
  'grok4': {
    replacement: 'grok4-3',
    sunsetDate: '2026-05-15',
    reason: "xAI retired Grok 4 (grok-4-0709) on 2026-05-15. Switching to Grok 4.3.",
  },
  'grok4-fast': {
    replacement: 'gpt-6-luna',
    sunsetDate: '2026-05-15',
    reason: "xAI retired Grok 4 Fast on 2026-05-15. Switching to GPT-6 Luna (closest price and speed).",
  },
};

/** True iff `apiName` is on the active deprecation list. */
export function isDeprecated(apiName: string | null | undefined): boolean {
  if (!apiName) return false;
  return Object.prototype.hasOwnProperty.call(DEPRECATED_MODELS, apiName);
}

/** If `apiName` is deprecated, return its replacement; otherwise return null. */
export function resolveDeprecation(
  apiName: string | null | undefined,
  toolKind: 'text' | 'image' | 'voice' | 'video' = 'text',
): { replacement: string; info: DeprecationInfo } | null {
  if (!apiName) return null;
  const info = DEPRECATED_MODELS[apiName];
  if (!info) return null;
  const replacement =
    toolKind === 'image' && info.imageReplacement ? info.imageReplacement : info.replacement;
  return { replacement, info };
}

/** Strip deprecated entries from a list, preserving order. */
export function filterDeprecated(apiNames: string[] | null | undefined): string[] {
  return (apiNames || []).filter(n => !isDeprecated(n));
}

/** Filter a model list to ZDR-eligible entries, preserving order. */
export function filterZDREligible(apiNames: string[] | null | undefined): string[] {
  return (apiNames || []).filter(isZDREligible);
}

/**
 * Helper for components that have to choose what to show when ZDR is on:
 * pass the raw set of options, get back the ZDR-pruned set. When ZDR is
 * off this is a passthrough.
 */
export function zdrFilterOptions<T extends { value: string }>(
  options: T[],
  zdrEnabled: boolean,
): T[] {
  if (!zdrEnabled) return options;
  return options.filter(o => isZDREligible(o.value));
}

/** Claude models that take a thinking-effort level and ignore temperature (backend CLAUDE_NO_SAMPLING_MODELS). */
export const CLAUDE_EFFORT_MODELS: ReadonlySet<string> = new Set([
  'claude-opus-4-7',
  'claude-opus-4-8',
  'claude-opus-5',
  'claude-sonnet-5',
  'claude-sonnet-5-5',
  'claude-opus-5-5',
  'claude-fable-5-1',
]);

export function supportsEffort(apiName?: string | null): boolean {
  return CLAUDE_EFFORT_MODELS.has((apiName || '').toLowerCase());
}

// Convenience functions
export type TemperaturePolicy = {
  mode: 'adjustable' | 'locked' | 'unsupported';
  /** Provider-enforced value when mode is 'locked'. */
  lockedValue?: number;
  hint: string;
};

/**
 * Whether a chain-node temperature override can actually reach the model.
 * GPT-5 only accepts temperature=1; o-series reasoning models reject the param.
 */
export function temperaturePolicy(apiName?: string | null): TemperaturePolicy {
  const n = (apiName || '').toLowerCase();
  if (!n) {
    return {
      mode: 'adjustable',
      hint: 'Lower = more repeatable. 0 is the Gemini/GPT-4 default. Unset uses the model default.',
    };
  }
  if (CLAUDE_EFFORT_MODELS.has(n)) {
    return {
      mode: 'unsupported',
      hint: 'This Claude model sets its own sampling, so temperature is ignored. Use thinking effort to trade speed for depth.',
    };
  }
  if (n.startsWith('gpt-5')) {
    return {
      mode: 'locked',
      lockedValue: 1,
      hint: 'GPT-5 models only accept temperature=1. Switch to Gemini or GPT-4 to make this step more repeatable.',
    };
  }
  if (n.startsWith('o1') || n.startsWith('o3') || n.startsWith('o4')) {
    return {
      mode: 'unsupported',
      hint: 'Reasoning models ignore temperature. Sampling is not configurable for this model.',
    };
  }
  return {
    mode: 'adjustable',
    hint: 'Lower = more focused/repeatable, higher = more creative. 0 is the most deterministic this model allows.',
  };
}

export function getModelInfo(apiName: string): ModelInfo | undefined {
  const info = MODEL_REGISTRY[apiName];
  if (!info) return undefined;
  // Stamp `zeroDataRetention` derived from the canonical eligibility set
  // so the rest of the UI doesn't have to re-derive it.
  if (info.zeroDataRetention === undefined) {
    return { ...info, zeroDataRetention: isZDREligible(apiName) };
  }
  return info;
}

export function getDisplayName(apiName: string): string {
  const modelInfo = getModelInfo(apiName);
  return modelInfo?.displayName || apiName;
}

/** Label + tooltip for a chain-run step. Uses the model that actually ran
 * (`modelUsed`). If the node was configured with a deprecated tag that was
 * remapped, `modelRequested` is the original and the tooltip says so. */
export function describeRunModel(
  modelUsed?: string | null,
  modelRequested?: string | null,
  fallbackUsed?: boolean,
): { label: string | null; tooltip?: string } {
  if (!modelUsed) return { label: null };
  const label = modelUsed === 'multiple' ? 'Multiple models' : getDisplayName(modelUsed);
  const parts: string[] = [];
  if (modelRequested && modelRequested !== modelUsed) {
    parts.push(`Configured as ${getDisplayName(modelRequested)}; ran ${label}`);
  }
  if (fallbackUsed) {
    parts.push('Fell back from the requested model');
  }
  return { label, tooltip: parts.length > 0 ? parts.join(' · ') : modelUsed };
}

/** Models we never want surfaced in selectors: not-yet-available OR
 * actively-deprecated (auto-remapped at request time). Centralized so
 * every helper below has a single source of truth. */
function _isSelectableModel(m: ModelInfo): boolean {
  return Boolean(m.isAvailable) && !isDeprecated(m.apiName);
}

export function getTextModels(): ModelInfo[] {
  return Object.values(MODEL_REGISTRY).filter(m => m.category === 'text' && _isSelectableModel(m));
}

export function getModelsByProvider(provider: string): ModelInfo[] {
  return Object.values(MODEL_REGISTRY).filter(m => m.provider === provider && _isSelectableModel(m));
}

export function getVisionCapableModels(): ModelInfo[] {
  return Object.values(MODEL_REGISTRY).filter(m => m.supportsVision && _isSelectableModel(m));
}

export function getImageGenerationModels(): ModelInfo[] {
  return Object.values(MODEL_REGISTRY).filter(m => m.category === 'image' && _isSelectableModel(m));
}

export function getEmbeddingModels(): ModelInfo[] {
  return Object.values(MODEL_REGISTRY).filter(m => m.category === 'embedding' && _isSelectableModel(m));
}

export function getAudioModels(): ModelInfo[] {
  return Object.values(MODEL_REGISTRY).filter(m => m.category === 'audio' && _isSelectableModel(m));
}

export function getVideoModels(): ModelInfo[] {
  return Object.values(MODEL_REGISTRY).filter(m => m.category === 'video' && _isSelectableModel(m));
}

// Video model identifiers
export const VIDEO_MODELS = Object.keys(MODEL_REGISTRY).filter(
  key => MODEL_REGISTRY[key].category === 'video'
);

// Helper to check if a model is a video generation model
export function isVideoModel(modelName: string): boolean {
  return VIDEO_MODELS.includes(modelName) || 
         VIDEO_MODELS.some(vm => modelName.toLowerCase().includes(vm.toLowerCase()));
}

// Helper to check if any model in a list is a video model
export function hasVideoModel(models: string[]): boolean {
  return models.some(m => isVideoModel(m));
}

export function getModelCapabilitySummary(model: ModelInfo): string {
  if (model.category === 'image') return 'image output';
  if (model.category === 'video') return 'video output';
  if (model.category === 'audio') return 'audio';
  if (model.category === 'embedding') return 'embeddings';
  return model.supportsVision ? 'text + vision' : 'text';
}

function inferProviderFromModelName(apiName: string): string {
  const normalized = apiName.toLowerCase();

  if (
    normalized.startsWith('gpt-') ||
    normalized.startsWith('o1') ||
    normalized.startsWith('o3') ||
    normalized.startsWith('o4') ||
    normalized.startsWith('dall-e') ||
    normalized.startsWith('whisper') ||
    normalized.startsWith('openai-')
  ) {
    return 'OpenAI';
  }
  if (
    normalized.startsWith('gemini') ||
    normalized.startsWith('imagen') ||
    normalized.startsWith('veo') ||
    normalized.startsWith('nano-banana')
  ) {
    return 'Google';
  }
  if (normalized.startsWith('grok')) {
    return 'xAI';
  }
  if (normalized.startsWith('claude')) {
    return 'Anthropic';
  }
  if (normalized.startsWith('llama')) {
    return 'Meta';
  }
  if (normalized.startsWith('mistral') || normalized.startsWith('codestral')) {
    return 'Mistral';
  }
  if (normalized.startsWith('deepseek')) {
    return 'DeepSeek';
  }

  return 'Unknown';
}

function inferCategoryFromModelName(apiName: string): ModelInfo['category'] | 'text' {
  const normalized = apiName.toLowerCase();

  if (
    normalized.startsWith('gpt-image') ||
    normalized.startsWith('dall-e') ||
    normalized.startsWith('imagen') ||
    normalized.includes('image-generation') ||
    normalized.startsWith('nano-banana')
  ) {
    return 'image';
  }
  if (normalized.startsWith('veo') || normalized.startsWith('sora')) {
    return 'video';
  }
  if (normalized.startsWith('whisper')) {
    return 'audio';
  }
  if (normalized.includes('embedding')) {
    return 'embedding';
  }

  return 'text';
}

function inferCapabilityLabel(apiName: string): string {
  const category = inferCategoryFromModelName(apiName);
  if (category === 'image') return 'image output';
  if (category === 'video') return 'video output';
  if (category === 'audio') return 'audio';
  if (category === 'embedding') return 'embeddings';
  return 'custom';
}

export interface ModelOption {
  value: string;
  label: string;
  provider: string;
  capabilityLabel: string;
  category: ModelInfo['category'] | 'text';
  costRating?: number;
  speedRating?: number;
  intelligenceRating?: number;
}

/** Sort models: by provider, then category (text, image, video, etc.), then family tier, then version desc. */
function sortModelOptions(options: ModelOption[]): ModelOption[] {
  const providerOrder: Record<string, number> = { Google: 0, OpenAI: 1, xAI: 2, Anthropic: 3, Meta: 4, Mistral: 5, DeepSeek: 6 };
  const categoryOrder: Record<string, number> = { text: 0, image: 1, video: 2, audio: 3, embedding: 4 };

  const tierOf = (name: string): number => {
    const n = name.toLowerCase();
    if (n.includes('pro') || n.includes('opus')) return 0;
    if (n.includes('sonnet') || n.includes('medium')) return 1;
    if (n.includes('flash') && !n.includes('lite')) return 2;
    if (n.includes('haiku') || n.includes('small') || n.includes('mini') || n.includes('nano')) return 3;
    if (n.includes('lite')) return 4;
    if (n.includes('ultra')) return 0;
    if (n.includes('fast')) return 3;
    return 1;
  };

  return [...options].sort((a, b) => {
    const pa = providerOrder[a.provider] ?? 99;
    const pb = providerOrder[b.provider] ?? 99;
    if (pa !== pb) return pa - pb;
    const ca = categoryOrder[a.category] ?? 99;
    const cb = categoryOrder[b.category] ?? 99;
    if (ca !== cb) return ca - cb;
    const ta = tierOf(a.value);
    const tb = tierOf(b.value);
    if (ta !== tb) return ta - tb;
    const ia = a.intelligenceRating ?? 0;
    const ib = b.intelligenceRating ?? 0;
    if (ib !== ia) return ib - ia;
    return a.label.localeCompare(b.label);
  });
}

// Build model options for select dropdowns
export function getModelOptions(filterApiNames?: string[]): ModelOption[] {
  const models = filterApiNames 
    ? filterApiNames.map(apiName => MODEL_REGISTRY[apiName]).filter(Boolean)
    : getTextModels();
    
  const options = models.map(model => ({
    value: model.apiName,
    label: model.displayName,
    provider: model.provider,
    capabilityLabel: getModelCapabilitySummary(model),
    category: model.category,
    costRating: model.costRating,
    speedRating: model.speedRating,
    intelligenceRating: model.intelligenceRating,
  }));
  return sortModelOptions(options);
}

/** Build options from allowed_models list; includes models not in registry (raw display). */
export function getModelOptionsForAllowedModels(allowedModels: string[]): ModelOption[] {
  if (!allowedModels?.length) return [];
  return allowedModels.map(apiName => {
    const info = MODEL_REGISTRY[apiName];
    return info
      ? {
          value: info.apiName,
          label: info.displayName,
          provider: info.provider,
          capabilityLabel: getModelCapabilitySummary(info),
          category: info.category,
          costRating: info.costRating,
          speedRating: info.speedRating,
          intelligenceRating: info.intelligenceRating,
        }
      : {
          value: apiName,
          label: apiName,
          provider: inferProviderFromModelName(apiName),
          capabilityLabel: inferCapabilityLabel(apiName),
          category: inferCategoryFromModelName(apiName),
        };
  });
}

/** Build an array of booleans for rendering a 1–5 bar (true = filled). */
export function ratingBars(rating: number | undefined, max = 5): boolean[] {
  if (!rating) return [];
  const filled = Math.min(Math.max(Math.round(rating), 0), max);
  return Array.from({ length: max }, (_, i) => i < filled);
}

export interface GroupedModelOptions {
  provider: string;
  models: ModelOption[];
}

const PROVIDER_SORT_ORDER: Record<string, number> = {
  'Google': 0, 'OpenAI': 1, 'Anthropic': 2, 'Meta': 3,
  'Mistral': 4, 'DeepSeek': 5, 'xAI': 6,
};

export function getGroupedModelOptions(options: ModelOption[]): GroupedModelOptions[] {
  const groups: Record<string, ModelOption[]> = {};
  for (const opt of options) {
    const p = opt.provider || 'Other';
    (groups[p] ??= []).push(opt);
  }
  
  // Sort models within each provider group
  for (const models of Object.values(groups)) {
    models.sort((a, b) => {
      // Extract model family and version info
      const familyA = extractModelFamily(a.label);
      const familyB = extractModelFamily(b.label);
      
      // First, group by model family (e.g., Gemini models together, Claude together, etc.)
      if (familyA !== familyB) {
        return familyA.localeCompare(familyB);
      }
      
      // Within the same family, sort by tier (Pro/Opus > Sonnet/Medium > Flash > Haiku/Small/Mini/Nano > Lite/Fast)
      const tierA = extractModelTier(a.label);
      const tierB = extractModelTier(b.label);
      if (tierA !== tierB) {
        return tierA - tierB;
      }
      
      // Within the same tier, sort by version (newest first)
      const versionA = extractModelVersion(a.label);
      const versionB = extractModelVersion(b.label);
      if (versionA !== versionB) {
        return versionB - versionA; // Descending (newer first)
      }
      
      // If still tied, sort by intelligence rating (higher first)
      const iq = (b.intelligenceRating ?? 0) - (a.intelligenceRating ?? 0);
      if (iq !== 0) return iq;
      
      // Finally, alphabetically by label
      return a.label.localeCompare(b.label);
    });
  }
  
  return Object.entries(groups)
    .sort(([a], [b]) => (PROVIDER_SORT_ORDER[a] ?? 99) - (PROVIDER_SORT_ORDER[b] ?? 99))
    .map(([provider, models]) => ({ provider, models }));
}

// Helper function to extract model family for grouping (e.g., "Gemini", "Claude", "Grok")
function extractModelFamily(label: string): string {
  const lower = label.toLowerCase();
  
  // Specific family patterns
  if (lower.includes('gemini')) return 'Gemini';
  if (lower.includes('claude')) return 'Claude';
  if (lower.includes('grok')) return 'Grok';
  if (lower.includes('llama')) return 'Llama';
  if (lower.includes('mistral') || lower.includes('codestral')) return 'Mistral';
  if (lower.includes('deepseek')) return 'DeepSeek';
  if (lower.includes('gpt-5') || lower.includes('gpt-4') || lower.includes('gpt-3')) return 'GPT';
  if (lower.includes('o3') || lower.includes('o4') || lower.includes('o1')) return 'OpenAI Reasoning';
  if (lower.includes('dall-e')) return 'DALL-E';
  if (lower.includes('gpt-image') || lower.includes('gpt image')) return 'GPT Image';
  if (lower.includes('imagen')) return 'Imagen';
  if (lower.includes('veo')) return 'Veo';
  if (lower.includes('nano banana')) return 'Nano Banana';
  if (lower.includes('whisper')) return 'Whisper';
  if (lower.includes('embedding')) return 'Embeddings';
  
  return label;
}

// Helper function to extract model tier for sorting (lower number = higher tier)
function extractModelTier(label: string): number {
  const lower = label.toLowerCase();
  
  // Premium/Most Capable
  if (lower.includes('pro') && lower.includes('5.2')) return 0;
  if (lower.includes('pro') || lower.includes('opus')) return 0;
  if (lower.includes('5.4') && !lower.includes('mini') && !lower.includes('nano')) return 0;
  if (lower.includes('ultra')) return 0;
  if (lower.includes('maverick')) return 0;
  if (lower.includes('3.1')) return 0;
  
  // Mid-tier
  if (lower.includes('sonnet') || lower.includes('medium') || lower.includes('5')) return 1;
  if (lower.includes('flash') && !lower.includes('lite') && !lower.includes('fast')) return 1;
  if (lower.includes('3.0')) return 1;
  if (lower.includes('4')) return 1;
  
  // Lightweight/Fast
  if (lower.includes('mini') && !lower.includes('ultra')) return 2;
  if (lower.includes('nano')) return 2;
  if (lower.includes('small')) return 2;
  if (lower.includes('flash') && lower.includes('lite')) return 2;
  if (lower.includes('haiku')) return 2;
  if (lower.includes('scout')) return 2;
  if (lower.includes('fast') && !lower.includes('flash')) return 2;
  
  // Economy variants
  if (lower.includes('lite')) return 3;
  if (lower.includes('fast') && (lower.includes('flash') || lower.includes('veo'))) return 3;
  
  // Default to middle
  return 1;
}

// Helper function to extract numeric version for sorting (newer first)
function extractModelVersion(label: string): number {
  const lower = label.toLowerCase();
  
  // Extract version numbers (e.g., "2.5" from "Gemini 2.5 Flash")
  const versionMatch = lower.match(/(\d+)\.(\d+)/);
  if (versionMatch) {
    const major = parseInt(versionMatch[1], 10);
    const minor = parseInt(versionMatch[2], 10);
    return major * 1000 + minor; // e.g., 2.5 -> 2500 + 5 = 2505
  }
  
  // Extract single version numbers (e.g., "5" from "GPT-5")
  const singleMatch = lower.match(/(\d+)(?:\s|$|[^.\d])/);
  if (singleMatch) {
    return parseInt(singleMatch[1], 10) * 1000;
  }
  
  return 0;
}

export function getAllModelOptions(): ModelOption[] {
  const allModels = [
    ...getTextModels(), ...getImageGenerationModels(), ...getVideoModels(),
    ...getEmbeddingModels(), ...getAudioModels(),
  ];
  return allModels.map(model => ({
    value: model.apiName,
    label: model.displayName,
    provider: model.provider,
    category: model.category,
    capabilityLabel: getModelCapabilitySummary(model),
    costRating: model.costRating,
    speedRating: model.speedRating,
    intelligenceRating: model.intelligenceRating,
  }));
}

// Legacy model name mapping for backward compatibility
export const MODEL_NAME_MAPPING: Record<string, string> = Object.fromEntries(
  Object.values(MODEL_REGISTRY).map(info => [info.apiName, info.llmName])
);
